"use client";

import { CHART_COLORS } from "@/lib/tool-kit/palette";
import {
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import { niceTicks } from "@/lib/tool-kit/stats-descriptive/ticks";

const WIDTH = 640;
const HEIGHT = 380;
const LEFT = 56;
const RIGHT = 20;
const TOP = 16;
const BOTTOM = 44;

/**
 * A scatter plot of (x, y) with the least-squares line drawn across the observed range
 * of x — not beyond it, because a line drawn past the data invites the extrapolation
 * the regression page warns against.
 */
export function ScatterPlot({
  xs,
  ys,
  intercept,
  slope,
  fmt,
  label,
  xLabel,
  yLabel,
}: {
  xs: readonly number[];
  ys: readonly number[];
  intercept: number;
  slope: number;
  fmt: NumberFormatter;
  label: string;
  xLabel: string;
  yLabel: string;
}) {
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const fitAtMin = intercept + slope * minX;
  const fitAtMax = intercept + slope * maxX;
  const xAxis = niceTicks(minX, maxX, 6);
  const yAxis = niceTicks(
    Math.min(...ys, fitAtMin, fitAtMax),
    Math.max(...ys, fitAtMin, fitAtMax),
    6
  );
  const plotWidth = WIDTH - LEFT - RIGHT;
  const plotHeight = HEIGHT - TOP - BOTTOM;
  const px = (value: number) =>
    LEFT + ((value - xAxis.lo) / (xAxis.hi - xAxis.lo || 1)) * plotWidth;
  const py = (value: number) =>
    TOP +
    plotHeight -
    ((value - yAxis.lo) / (yAxis.hi - yAxis.lo || 1)) * plotHeight;

  return (
    <svg
      aria-label={label}
      className="h-auto w-full text-muted-foreground"
      role="img"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
    >
      {yAxis.ticks.map((tick) => (
        <g key={`y${tick}`}>
          <line
            stroke="currentColor"
            strokeOpacity={0.2}
            x1={LEFT}
            x2={WIDTH - RIGHT}
            y1={py(tick)}
            y2={py(tick)}
          />
          <text
            dominantBaseline="middle"
            fill="currentColor"
            fontSize={12}
            textAnchor="end"
            x={LEFT - 8}
            y={py(tick)}
          >
            {formatStat(fmt, tick)}
          </text>
        </g>
      ))}
      {xAxis.ticks.map((tick) => (
        <g key={`x${tick}`}>
          <line
            stroke="currentColor"
            strokeOpacity={0.2}
            x1={px(tick)}
            x2={px(tick)}
            y1={TOP}
            y2={TOP + plotHeight}
          />
          <text
            fill="currentColor"
            fontSize={12}
            textAnchor="middle"
            x={px(tick)}
            y={TOP + plotHeight + 18}
          >
            {formatStat(fmt, tick)}
          </text>
        </g>
      ))}
      <text
        fill="currentColor"
        fontSize={12}
        textAnchor="middle"
        x={LEFT + plotWidth / 2}
        y={HEIGHT - 6}
      >
        {xLabel}
      </text>
      <text
        fill="currentColor"
        fontSize={12}
        textAnchor="middle"
        transform={`rotate(-90 14 ${TOP + plotHeight / 2})`}
        x={14}
        y={TOP + plotHeight / 2}
      >
        {yLabel}
      </text>
      <line
        stroke={CHART_COLORS[1]}
        strokeWidth={2.5}
        x1={px(minX)}
        x2={px(maxX)}
        y1={py(fitAtMin)}
        y2={py(fitAtMax)}
      />
      {xs.map((value, index) => (
        <circle
          cx={px(value)}
          cy={py(ys[index])}
          fill={CHART_COLORS[0]}
          fillOpacity={0.85}
          // Points can coincide, so position is the only stable identity.
          // biome-ignore lint/suspicious/noArrayIndexKey: data points by position
          key={index}
          r={4.5}
        />
      ))}
    </svg>
  );
}
